import React from 'react';
import { graphql, commitMutation } from 'react-relay';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  Header,
  Icon,
  Modal,
  Popup
} from 'semantic-ui-react';
import {
  FloatingLabelInput as Input,
  FloatingLabelSelect as Select
} from '../../Elements';
import {
  Analytics,
  GlobalNotifications,
  Intent,
  MobileModal
} from '../../Modules';
import Formsy from 'formsy-react';
import styles from '../Event.scss';
import filestack from 'filestack-js';
import { intersection } from 'lodash';
import { RootConsumer } from '../../RootComponent';

const apikey = process.env.FILESTACK_API_KEY;
const client = filestack.init(apikey);
const DOCUMENT_SECTIONS = {
  Contract: [
    'BRAND',
    'EVENT_SEGMENTS',
    'LINE_ITEMS',
    'TERMS',
    'SIGNATURE_BLOCKS'
  ],
  Estimate: ['BRAND', 'RECIPIENT', 'EVENT_SEGMENTS', 'LINE_ITEMS', 'NOTES'],
  Invoice: [
    'BRAND',
    'RECIPIENT',
    'EVENT_SEGMENTS',
    'LINE_ITEMS',
    'PAYMENT_PLAN'
  ],
  Form: ['BRAND', 'FORM'],
  MusicPlanner: ['BRAND', 'DESCRIPTION', 'PLAYLISTS', 'NOTES']
};
const SECTION_LABELS = {
  BRAND: 'Brand',
  DESCRIPTION: 'Description',
  RECIPIENT: 'Recipient',
  FORM: 'Form Questions',
  EVENT_SEGMENTS: 'Schedule',
  LINE_ITEMS: 'Line Items',
  NOTES: 'Notes',
  PLAYLISTS: 'Playlists',
  SIGNATURE_BLOCKS: 'Signature Blocks',
  TERMS: 'Terms',
  PAYMENT_PLAN: 'Payment Plan'
};

export default class NewDocument extends React.Component {
  static propTypes = {
    closeNewDocumentModal: PropTypes.func.isRequired,
    createWorkspace: PropTypes.func.isRequired,
    documentType: PropTypes.string,
    handleNewWorkspaceTitleChange: PropTypes.func.isRequired,
    handleTitleChange: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    newDocumentModalOpen: PropTypes.bool.isRequired,
    newWorkspaceTitle: PropTypes.string.isRequired,
    node: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
    title: PropTypes.string.isRequired,
    viewer: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedWorkspace:
        this.props.node && this.props.node.workspaces.edges.length > 0
          ? this.props.node.workspaces.edges[0].node.id
          : '__new_ws__',
      selectedContractTemplate: this.props.location.query.id || '__none__',
      selectedFormTemplate: this.props.location.query.id || '__none__',
      selectedMpTemplate: this.props.location.query.id || '__none__',
      selectedSections: [],
      selectedDocument: '__none__',
      lastUploaded: new Date()
    };

    this.getDocument = this.getDocument.bind(this);
    this.getCopiableSections = this.getCopiableSections.bind(this);
    this.getWorkspaceDocuments = this.getWorkspaceDocuments.bind(this);

    this.handleContractTemplateChange = this.handleContractTemplateChange.bind(
      this
    );
    this.handleWorkspaceChange = this.handleWorkspaceChange.bind(this);
    this.handleFormTemplateChange = this.handleFormTemplateChange.bind(this);
    this.handleMpTemplateChange = this.handleMpTemplateChange.bind(this);
    this.handleDocumentChange = this.handleDocumentChange.bind(this);

    this.onValidDocumentSubmit = this.onValidDocumentSubmit.bind(this);
    this.onInvalidDocumentSubmit = this.onInvalidDocumentSubmit.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onSectionToggle = this.onSectionToggle.bind(this);

    this.openPicker = this.openPicker.bind(this);
    this.createWupload = this.createWupload.bind(this);
    this.createContract = this.createContract.bind(this);
    this.createEstimate = this.createEstimate.bind(this);
    this.createInvoice = this.createInvoice.bind(this);
    this.createForm = this.createForm.bind(this);
    this.createMusicPlanner = this.createMusicPlanner.bind(this);
    this.isCopiable = this.isCopiable.bind(this);
  }

  onValidDocumentSubmit() {
    const createDocument = (documentType => {
      switch (documentType) {
        case 'contract':
          return this.createContract;
        case 'estimate':
          return this.createEstimate;
        case 'invoice':
          return this.createInvoice;
        case 'form':
          return this.createForm;
        case 'file':
          return this.openPicker;
        case 'music list':
          return this.createMusicPlanner;
        default:
          return this.createEstimate;
      }
    })(this.props.documentType);

    if (this.state.selectedWorkspace === '__new_ws__') {
      this.props.createWorkspace(createDocument);
    } else {
      createDocument(this.state.selectedWorkspace);
      this.onClose();
    }
  }

  onInvalidDocumentSubmit() {
    GlobalNotifications.show({
      message: `Unable to create ${this.props.documentType}`,
      intent: Intent.DANGER
    });
  }

  onClose() {
    this.setState({
      selectedDocument: '__none__',
      selectedContractTemplate: '__none__',
      selectedFormTemplate: '__none__',
      selectedMpTemplate: '__none__',
      selectedSections: [],
      selectedWorkspace:
        this.props.node && this.props.node.workspaces.edges.length > 0
          ? this.props.node.workspaces.edges[0].node.id
          : '__new_ws__'
    });
    this.props.closeNewDocumentModal();
  }

  onSectionToggle(e, data) {
    const selectedSections = this.state.selectedSections;
    if (data.checked) {
      this.setState({ selectedSections: selectedSections.concat(data.name) });
    } else {
      this.setState({
        selectedSections: selectedSections.filter(s => s !== data.name)
      });
    }
  }

  getDocument(id) {
    const matches = this.getWorkspaceDocuments().filter(
      document => document.node.id === id
    );
    return matches.length > 0 ? matches[0] : null;
  }

  getCopiableSections() {
    return intersection(
      DOCUMENT_SECTIONS[
        // eslint-disable-next-line no-underscore-dangle
        this.getDocument(this.state.selectedDocument).node.__typename
      ],
      DOCUMENT_SECTIONS[
        this.props.documentType.toLowerCase().split(' ').map((type) => (
          type.charAt(0).toUpperCase() + type.slice(1)
        ))
        .join('')
      ]
    );
  }

  getWorkspaceDocuments() {
    const workspaceDocuments = [];
    this.props.node.workspaces.edges.map(workspace => {
      const contracts = workspace.node.contracts.edges;
      const forms = workspace.node.forms.edges;
      const estimates = workspace.node.estimates.edges;
      const invoices = workspace.node.invoices.edges;
      const musicPlanners = workspace.node.music_planners.edges;
      if (contracts.length > 0) workspaceDocuments.push(...contracts);
      if (forms.length > 0) workspaceDocuments.push(...forms);
      if (estimates.length > 0) workspaceDocuments.push(...estimates);
      if (invoices.length > 0) workspaceDocuments.push(...invoices);
      if (musicPlanners.length > 0) workspaceDocuments.push(...musicPlanners);
      return workspace;
    });
    return workspaceDocuments;
  }

  handleContractTemplateChange(e) {
    this.setState({ selectedContractTemplate: e.target.value });
  }

  handleFormTemplateChange(e) {
    this.setState({ selectedFormTemplate: e.target.value });
  }

  handleMpTemplateChange(e) {
    this.setState({ selectedMpTemplate: e.target.value });
  }

  handleWorkspaceChange(e) {
    this.setState({ selectedWorkspace: e.target.value });
  }

  handleDocumentChange(e) {
    this.setState({
      selectedDocument: e.target.value,
      selectedSections:
        e.target.value === '__none__'
          ? []
          : // eslint-disable-next-line no-underscore-dangle
            DOCUMENT_SECTIONS[this.getDocument(e.target.value).node.__typename]
    });
  }

  isCopiable(documentType) {
    return [
      'contract',
      'estimate',
      'form',
      'music list',
      'invoice'
    ].includes(documentType);
  }

  openPicker(workspaceId) {
    client
      .pick({
        maxFiles: 5,
        maxSize: 250000000,
        fromSources: ['local_file_system'],
        storeTo: {
          location: 's3'
        }
      })
      .then(res => {
        res.filesUploaded.map(file => {
          this.createWupload(
            {
              name: file.filename,
              url: `https://s3.amazonaws.com/cb-user-uploads/${file.key}`,
              mimetype: file.mimetype,
              source: file.source,
              size: file.size,
              handle: file.handle
            },
            workspaceId
          );
          return file;
        });
      });
  }

  createContract(workspaceId) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to create contract',
        intent: Intent.DANGER
      });
    };

    const onCompleted = response => {
      GlobalNotifications.show({
        message: 'Successfully created contract',
        intent: Intent.SUCCESS
      });
      this.props.router.push(
        `/docs/c/${response.createContract.contract.id}/edit`
      );
      Analytics.trackEvent('created-contract', { origin: 'overview' });
      this.onClose();
    };

    const cTemplateId =
      this.state.selectedContractTemplate !== '__none__'
        ? this.state.selectedContractTemplate
        : null;

    const mutation = graphql`
      mutation NewDocument_CreateContractMutation(
        $input: CreateContractInput!
      ) {
        createContract(input: $input) {
          contract {
            id
            title
          }
        }
      }
    `;

    const environment = this.props.relay.environment;
    const copyDocument =
      this.state.selectedSections.length === 0
        ? null
        : {
          document_id: this.getDocument(this.state.selectedDocument).node.id,
          include_only: this.state.selectedSections
        };

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          copy_document: copyDocument,
          workspace_ids: [workspaceId],
          title: this.props.title,
          copy_user_event_segments: true,
          add_default_signature_block: false,
          c_template_id: cTemplateId
        }
      },
      onCompleted,
      onError
    });
  }

  createEstimate(workspaceId) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to create estimate',
        intent: Intent.DANGER
      });
    };

    const onCompleted = response => {
      GlobalNotifications.show({
        message: 'Successfully created estimate',
        intent: Intent.SUCCESS
      });
      this.props.router.push(
        `/docs/e/${response.createEstimate.estimate.id}/edit`
      );
      Analytics.trackEvent('created-estimate', { origin: 'overview' });
      this.onClose();
    };

    const mutation = graphql`
      mutation NewDocument_CreateEstimateMutation(
        $input: CreateEstimateInput!
      ) {
        createEstimate(input: $input) {
          estimate {
            id
            title
          }
        }
      }
    `;

    const environment = this.props.relay.environment;
    const copyDocument =
      this.state.selectedSections.length === 0
        ? null
        : {
          document_id: this.getDocument(this.state.selectedDocument).node.id,
          include_only: this.state.selectedSections
        };

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          copy_document: copyDocument,
          workspace_ids: [workspaceId],
          title: this.props.title,
          copy_user_event_segments: true
        }
      },
      onCompleted,
      onError
    });
  }

  createInvoice(workspaceId) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to create invoice',
        intent: Intent.DANGER
      });
    };

    const onCompleted = response => {
      GlobalNotifications.show({
        message: 'Successfully created invoice',
        intent: Intent.SUCCESS
      });
      this.props.router.push(
        `/docs/i/${response.createInvoice.invoice.id}/edit`
      );
      Analytics.trackEvent('created-invoice', { origin: 'overview' });
      this.onClose();
    };

    const mutation = graphql`
      mutation NewDocument_CreateInvoiceMutation($input: CreateInvoiceInput!) {
        createInvoice(input: $input) {
          invoice {
            id
            title
          }
        }
      }
    `;

    const environment = this.props.relay.environment;
    const copyDocument =
      this.state.selectedSections.length === 0
        ? null
        : {
          document_id: this.getDocument(this.state.selectedDocument).node.id,
          include_only: this.state.selectedSections
        };

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          copy_document: copyDocument,
          workspace_ids: [workspaceId],
          title: this.props.title,
          copy_user_event_segments: true
        }
      },
      onCompleted,
      onError
    });
  }

  createForm(workspaceId) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to create form',
        intent: Intent.DANGER
      });
    };

    const onCompleted = response => {
      GlobalNotifications.show({
        message: 'Successfully created form',
        intent: Intent.SUCCESS
      });
      this.props.router.push(`/docs/f/${response.createForm.form.id}/edit`);
      Analytics.trackEvent('created-form', { origin: 'overview' });
      this.onClose();
    };

    const mutation = graphql`
      mutation NewDocument_CreateFormMutation($input: CreateFormInput!) {
        createForm(input: $input) {
          form {
            id
            title
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    const formTemplateId =
      this.state.selectedFormTemplate !== '__none__'
        ? this.state.selectedFormTemplate
        : null;

    const copyDocument =
      this.state.selectedSections.length === 0
        ? null
        : {
          document_id: this.getDocument(this.state.selectedDocument).node.id,
          include_only: this.state.selectedSections
        };

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          copy_document: copyDocument,
          workspace_id: workspaceId,
          title: this.props.title,
          form_template_id: formTemplateId,
          data: formTemplateId ? null : JSON.stringify({}),
          schema: formTemplateId
            ? null
            : JSON.stringify({
              type: 'object',
              properties: {}
            }),
          ui_schema: formTemplateId
            ? null
            : JSON.stringify({
              'ui:order': []
            })
        }
      },
      onCompleted,
      onError
    });
  }

  createMusicPlanner(workspaceId) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to create music list',
        intent: Intent.DANGER
      });
    };

    const onCompleted = response => {
      GlobalNotifications.show({
        message: 'Successfully created music list',
        intent: Intent.SUCCESS
      });
      this.props.router.push(
        `/docs/m/${response.createMusicPlanner.music_planner.id}/edit`
      );
      Analytics.trackEvent('created-music-planner', { origin: 'overview' });
      this.onClose();
    };

    const mutation = graphql`
      mutation NewDocument_CreateMusicPlannerMutation(
        $input: CreateMusicPlannerInput!
      ) {
        createMusicPlanner(input: $input) {
          music_planner {
            id
            title
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    const mpTemplateId =
      this.state.selectedMpTemplate !== '__none__'
        ? this.state.selectedMpTemplate
        : null;

    const copyDocument =
      this.state.selectedSections.length === 0
        ? null
        : {
          document_id: this.getDocument(this.state.selectedDocument).node.id,
          include_only: this.state.selectedSections
        };

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          copy_document: copyDocument,
          mp_template_id: mpTemplateId,
          workspace_id: workspaceId,
          title: this.props.title
        }
      },
      onCompleted,
      onError
    });
  }

  createWupload(fields, workspaceId) {
    const onError = () => {
      if (new Date() - this.state.lastUploaded > 3000) {
        GlobalNotifications.show({
          message: 'Unable to upload file',
          intent: Intent.DANGER
        });
        this.setState({ lastUploaded: new Date() });
      }
    };
    const onCompleted = () => {
      if (new Date() - this.state.lastUploaded > 3000) {
        GlobalNotifications.show({
          message: 'Successfully uploaded files',
          intent: Intent.SUCCESS
        });
        this.setState({ lastUploaded: new Date() });
        Analytics.trackEvent('uploaded-file');
      }
    };

    const mutation = graphql`
      mutation NewDocument_CreateWuploadMutation($input: CreateWuploadInput!) {
        createWupload(input: $input) {
          workspace {
            wuploads {
              edges {
                node {
                  id
                  mimetype
                  name
                  url
                }
              }
            }
          }
          new_wupload_edge {
            node {
              id
              mimetype
              name
              url
            }
          }
        }
      }
    `;

    const environment = this.props.relay.environment;
    const parentID = workspaceId || this.state.selectedWorkspace;

    const configs = [
      {
        type: 'RANGE_ADD',
        parentID,
        edgeName: 'new_wupload_edge',
        connectionInfo: [
          {
            key: 'WorkspacesContainer_wuploads',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          workspace_id: parentID,
          ...fields
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  render() {
    const event = this.props.node;
    const workspaceOptions = event
      ? event.workspaces.edges
          .slice()
          .map(workspace => ({
            text: workspace.node.title,
            value: workspace.node.id
          }))
          .concat({ text: 'Add New', value: '__new_ws__' })
      : [];

    const contractTemplateOptions = [
      { text: 'None', value: '__none__' }
    ].concat(
      this.props.viewer
        ? this.props.viewer.c_templates.edges.slice().map(template => ({
          text: template.node.title,
          value: template.node.id
        }))
        : []
    );

    const formTemplateOptions = [{ text: 'None', value: '__none__' }].concat(
      this.props.viewer
        ? this.props.viewer.form_templates.edges.slice().map(template => ({
          text: template.node.title,
          value: template.node.id
        }))
        : []
    );

    const mpTemplateOptions = [{ text: 'None', value: '__none__' }].concat(
      this.props.viewer
        ? this.props.viewer.mp_templates.edges.slice().map(template => ({
          text: template.node.title,
          value: template.node.id
        }))
        : []
    );

    const helpIcon = <Icon name='help circle' />;

    const workspaceHelpContent = `A workspace is a folder that you can
    optionally share with your client, team, or other vendors.`;

    const documentOptions = [{ text: 'None', value: '__none__' }].concat(
      this.getWorkspaceDocuments().map(workspaceDocument => ({
        value: workspaceDocument.node.id,
        text: workspaceDocument.node.title
      }))
    );

    const createDocumentForm = (
      <Formsy.Form
        onValidSubmit={this.onValidDocumentSubmit}
        onInvalidSubmit={this.onInvalidDocumentSubmit}
        ref='form'
      >
        {this.props.documentType !== 'file' &&
          <div>
            <h3>
              Give your {this.props.documentType} a title
            </h3>
            <Input
              label='Title'
              name='title'
              value={this.props.title}
              onChange={this.props.handleTitleChange}
              required
              validationError='Title is required'
              submitted={this.state.submitted}
            />
          </div>}
        {this.props.documentType === 'contract' &&
          this.props.viewer.c_templates.edges.length > 0 &&
          <div>
            <h3>Select a template</h3>
            <Select
              label='Template'
              name='c_template_id'
              value={this.state.selectedContractTemplate || '__none__'}
              onChange={this.handleContractTemplateChange}
              submitted={this.state.submitted}
              options={contractTemplateOptions}
              fluid
            />
          </div>}
        {this.props.documentType === 'form' &&
          this.props.viewer.form_templates.edges.length > 0 &&
          <div>
            <h3>Select a template</h3>
            <Select
              label='Template'
              name='form_template_id'
              value={this.state.selectedFormTemplate || '__none__'}
              onChange={this.handleFormTemplateChange}
              submitted={this.state.submitted}
              options={formTemplateOptions}
              fluid
            />
          </div>}
        {this.props.documentType === 'music list' &&
          this.props.viewer.mp_templates.edges.length > 0 &&
          <div>
            <h3>Select a template</h3>
            <Select
              label='Template'
              name='mp_template_id'
              value={this.state.selectedMpTemplate || '__none__'}
              onChange={this.handleMpTemplateChange}
              submitted={this.state.submitted}
              options={mpTemplateOptions}
              fluid
            />
          </div>}
        {this.getWorkspaceDocuments().length > 0 &&
          this.isCopiable(this.props.documentType) &&
          <div>
            <h3>Copy information from existing document</h3>
            <Select
              label='Document'
              name='document_id'
              value={this.state.selectedDocument || '__none__'}
              onChange={this.handleDocumentChange}
              submitted={this.state.submitted}
              options={documentOptions}
              fluid
            />
            {this.getDocument(this.state.selectedDocument) &&
              <div>
                {this.getCopiableSections().map((section, index) =>
                  <div key={index} style={{ marginTop: '1em' }}>
                    <Checkbox
                      checked={this.state.selectedSections.includes(section)}
                      label={SECTION_LABELS[section]}
                      name={section}
                      onChange={this.onSectionToggle}
                    />
                  </div>
                )}
              </div>}
          </div>}
        <h3>
          Save {this.props.documentType} to a workspace {' '}
          <Popup
            trigger={helpIcon}
            on='hover'
            inverted
            content={workspaceHelpContent}
            size='large'
          />
        </h3>
        <Select
          label='Workspace'
          name='workspace'
          value={this.state.selectedWorkspace}
          onChange={this.handleWorkspaceChange}
          submitted={this.state.submitted}
          placeholder='Type...'
          options={workspaceOptions}
          fluid
        />
        {this.state.selectedWorkspace === '__new_ws__' &&
          <Input
            label='Workspace Title'
            name='workspace'
            placeholder='(Eg. Client)'
            value={this.props.newWorkspaceTitle}
            onChange={this.props.handleNewWorkspaceTitleChange}
            required
            validationError='Title is required'
            submitted={this.state.submitted}
          />}
      </Formsy.Form>
    );

    return (
      <RootConsumer>
        {({ isMobile }) =>
          <div>
            {!isMobile &&
              <Modal
                open={this.props.newDocumentModalOpen}
                onClose={this.onClose}
                size='small'
              >
                <Header content={`Create ${this.props.documentType}`} />
                <Modal.Content>
                  <div style={{ maxWidth: '480px', margin: '0 auto' }}>
                    {createDocumentForm}
                  </div>
                </Modal.Content>
                <Modal.Actions>
                  <a onClick={this.onClose} className={styles.modalCancel}>
                    Cancel
                  </a>
                  <Button
                    primary
                    content={`Create ${this.props.documentType}`}
                    onClick={() => this.refs.form.submit()}
                  />
                </Modal.Actions>
              </Modal>}
            {isMobile &&
              <MobileModal
                open={this.props.newDocumentModalOpen}
                onClose={this.onClose}
              >
                <MobileModal.Header>
                  <div className='menu'>
                    <a className='item' onClick={this.onClose}>
                      Cancel
                    </a>
                  </div>
                </MobileModal.Header>
                <MobileModal.Content>
                  {createDocumentForm}
                  <Button
                    primary
                    fluid
                    onClick={() => this.refs.form.submit()}
                    content={`Create ${this.props.documentType}`}
                  />
                </MobileModal.Content>
              </MobileModal>}
          </div>}
      </RootConsumer>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Event/Show/NewDocument.js